import { Component, OnInit } from '@angular/core';
import { AddressDto, ConnectionPointAccessKeyDto, ConnectionPointCategoryDto, ConnectionPointDto, ConnectionPointService } from '@app/api/nts';
import { ConnectionRight } from '@shared/interfaces/nts/connection-right';
import { ControlsOf, FormGroupType } from '@app/core/utils/formgroup-type';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { addressFormGroup } from '@shared/forms/address.formgroup';
import { TabsComponent } from '@shared/components/tabs/tabs.component';
import { TabComponent } from '@shared/components/tabs/tab/tab.component';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { AddressComponent } from '@shared/components/address/address.component';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';
import { ConnectionRightFormComponent } from '@shared/components/connection-right-form/connection-right-form.component';
import { createConnectionRightForm } from '@shared/forms/connection-right.formgroup';
import { ModalService } from '@app/core/services/modal.service';
import { ToastService } from '@app/core/services/toast.service';
import { forkJoin, Observable } from 'rxjs';
import { removeNullValues } from '@app/core/utils/remove-null-values';
import { systemServiceTypeOptions, systemServicesCapacityValidators } from '@shared/forms/system-services.form';
import { InputNumberDirective } from '@shared/components/input/number/input-number.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';

@Component({
  selector: 'app-connectionpoint-edit',
  imports: [
    TabsComponent,
    TabComponent,
    ReactiveFormsModule,
    InputDirective,
    InputNumberDirective,
    SelectDirective,
    InputGroupComponent,
    LabelDirective,
    TranslatePipe,
    AddressComponent,
    ConnectionRightFormComponent,
  ],
  templateUrl: './connectionpoint-edit.component.html',
  styleUrl: './connectionpoint-edit.component.css'
})
export class ConnectionpointEditComponent implements OnInit, HasUnsavedChanges {
  public hasUnsavedChanges(): boolean {
    return this.form.dirty || this.connectionRightForm.dirty;
  }

  public form: FormGroupType<ConnectionPointDto> = new FormGroup<ControlsOf<ConnectionPointDto>>({
      id: new FormControl(null, Validators.required),
      identification: new FormControl(null, Validators.required),
    alternativeIdentification: new FormControl(null),
    systemServicesType: new FormControl(null),
    systemServicesCapacity: new FormControl<number | null>(null, systemServicesCapacityValidators),
    stationNumber: new FormControl(null),
    connectionPointCategoryId: new FormControl(null, Validators.required),
      description: new FormControl(null),
      hasSharedServiceLine: new FormControl(false),
      hasCollectiveMetering: new FormControl(false),
      numberOfInstallations: new FormControl<number | null>(null, [Validators.min(1), Validators.pattern(/^[1-9]\d*$/)]),
      categoryOfInstallationsId: new FormControl<string | null>(null),
      address: new FormGroup(addressFormGroup),
      accessKey: new FormGroup<ControlsOf<ConnectionPointAccessKeyDto>>({
        number: new FormControl<string | null>(null),
        placementType: new FormControl<string | null>(null),
        code: new FormControl<string | null>(null),
        remarks: new FormControl<string | null>(null),
        facility: new FormControl<string | null>(null),
      } as any),
    }
  )

  public id?: string;
  public connectionPoint?: ConnectionPointDto;
  public connectionRight?: ConnectionRight;
  public connectionRightForm = createConnectionRightForm();
  public connectionRightExpired = false;

  public connectionpointCategories: ConnectionPointCategoryDto[] = [];
  public connectionPointCategoryList: KeyValue[] = [];
  public systemServiceTypeOptions = systemServiceTypeOptions;

  constructor(
    private readonly connectionPointService: ConnectionPointService,
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
  ) {
  }

  public ngOnInit(): void {
    this.loadConnectionRight();

    this.connectionPointService.connectionPointCategoriesGet().subscribe({
        next: (data: ConnectionPointCategoryDto[]) => {
          this.connectionpointCategories = data;

          this.connectionPointCategoryList = data.map(category => ({
            key: category.id ?? '',
            value: category.name ?? ''
          }));
        },
        complete: () => {
          if (this.connectionPoint) {
            this.form.patchValue({
              ...this.connectionPoint,
              accessKey: this.connectionPoint.accessKey ?? {} as any,
            });
          } else if (this.id) {
            this.connectionPointService.connectionPointGetGet(this.id).subscribe({
                next: (result) => {
                    this.connectionPoint = result;
                    this.form.patchValue({
                      ...result,
                      accessKey: result.accessKey ?? {} as any,
                    });
                    this.loadConnectionRight();
                }
              }
            )
          }
        }
      },
    )
  }

  public addressChanged(address: AddressDto): void {
    const control = this.form.get('address');

    if (!control || !address) {
      return;
    }

    // The address component also emits while the form is being populated, so only react
    // when a value actually differs. patchValue leaves the form pristine, and submit()
    // skips the request entirely unless the form is dirty.
    const current = (control.value ?? {}) as Record<string, unknown>;
    const incoming = address as Record<string, unknown>;
    const hasChanges = Object.keys(incoming).some(key => incoming[key] !== current[key]);

    if (!hasChanges) {
      return;
    }

    control.patchValue(address);
    control.markAsDirty();
  }

  public submit(): void {
    const connectionPointChanged = this.form.dirty;
    const connectionRightChanged = !!this.connectionPoint?.connectionPointOwnsConnectionRights
      && !this.connectionRightExpired && this.connectionRightForm.dirty && this.connectionRightForm.enabled;

    if ((connectionPointChanged && !this.form.valid) || (connectionRightChanged && !this.connectionRightForm.valid)) {
      if (connectionPointChanged) {
        this.form.markAllAsTouched();
      }
      if (connectionRightChanged) {
        this.connectionRightForm.markAllAsTouched();
      }
      return;
    }

    const requests: Observable<unknown>[] = [];
    if (connectionPointChanged) {
      requests.push(this.connectionPointService.connectionPointEditPost(removeNullValues(this.form.value)));
    }
    if (connectionRightChanged && this.connectionPoint?.id) {
      const connectionRight = this.connectionRightForm.getRawValue();
      requests.push(this.connectionPointService.connectionPointConnectionPointIdConnectionRightPost(
        this.connectionPoint.id,
        connectionRight,
      ));
    }

    if (requests.length === 0) {
      this.modalService.close();
      return;
    }

    forkJoin(requests).subscribe(() => {
      if (connectionPointChanged) {
        this.toastService.show('connectionpoint.edit.success');
      }
      if (connectionRightChanged) {
        this.toastService.show('connectionRight.success');
      }
      this.modalService.close(true);
    });
  }

  private loadConnectionRight(): void {
    const connectionPointId = this.connectionPoint?.id ?? this.id;
    if (!connectionPointId || !this.connectionPoint?.connectionPointOwnsConnectionRights) {
      return;
    }

    this.connectionRightExpired = false;
    this.connectionRightForm.disable({ emitEvent: false });
    this.connectionPointService.connectionPointConnectionPointIdConnectionRightGet(connectionPointId)
      .subscribe(right => {
        this.connectionRight = right ?? undefined;
        this.connectionRightForm = createConnectionRightForm(this.connectionRight);
        this.connectionRightForm.markAsPristine();
        this.connectionRightExpired = right?.isExpired === true || right?.isAutomaticallyExpired === true;
        if (this.connectionRightExpired) {
          this.connectionRightForm.disable({ emitEvent: false });
        }
      });
  }

}
